import React, { useCallback, useEffect, useRef } from "react"
import PropTypes from "prop-types"

// Utils
import { getKeyCode } from "lib/utils/KeyCodes"

// Configs
import KC from "components/Stacker/config/KeyConfig"

import "./DropDownItems.scss"

function DropDownItems({
  id,
  items,
  action,
  handleClose,
  labelKey,
  disableAutoFocus,
  keydownFocus,
}) {
  const itemIndexes = useRef({})

  const handleClick = useCallback(
    (e) => {
      e.preventDefault()
      const { index } = e.target.dataset
      action(items[index], index)
      handleClose()
    },
    [action, handleClose, items]
  )

  const handleFocus = useCallback((index) => {
    if (itemIndexes.current[`item_${index}`]) {
      itemIndexes.current[`item_${index}`].focus()
    }
  }, [])

  const selectKeyPressedEle = useCallback(
    (key) => {
      items.some((item, index) => {
        const firstLetter = String(item[labelKey]).substr(0, 1).toLowerCase()
        const newKey = String(key).toLowerCase()
        if (firstLetter === newKey) {
          handleFocus(index)
          return true
        }
        return false
      })
    },
    [handleFocus, items, labelKey]
  )

  const handleKeyDown = useCallback(
    (index) => (e) => {
      const type = getKeyCode(e.key, e.shiftKey)
      const total = items.length - 1 // adjusted for zero-index
      let newIndex
      switch (type) {
        case KC.TAB:
          handleClose()
          break
        case KC.ARROWDOWN:
          e.preventDefault()
          newIndex = index === total ? 0 : index + 1
          handleFocus(newIndex)
          break
        case KC.ARROWUP:
        case KC.BACKTAB:
          e.preventDefault()
          newIndex = index === 0 ? total : index - 1
          handleFocus(newIndex)
          break
        case KC.ESCAPE:
          e.preventDefault()
          handleClose()
          break
        default:
          selectKeyPressedEle(type)
          break
      }
    },
    [handleClose, handleFocus, items.length, selectKeyPressedEle]
  )

  useEffect(() => {
    if (!disableAutoFocus) {
      handleFocus(0)
    }
  }, [disableAutoFocus, handleFocus])

  useEffect(() => {
    if (keydownFocus) {
      const index = 0
      if (itemIndexes.current[`item_${index}`]) {
        itemIndexes.current[`item_${index}`].focus()
      }
    }
  }, [keydownFocus])

  return (
    <ul className="DropDownItems" id={id} aria-labelledby={id} role="menu">
      {items.map((item, index) => {
        const { icon, colorImage } = item
        const label = item[labelKey]
        return (
          <li className="drop-down-item" key={label} role="menuitem">
            <button
              data-index={index}
              onClick={handleClick}
              onKeyDown={handleKeyDown(index)}
              aria-label={`${label} ${index + 1} of ${items.length}`}
              ref={(el) => (itemIndexes.current[`item_${index}`] = el)}
              disabled={item.isDisabled}
            >
              {icon && (
                <span>
                  <img
                    data-index={index}
                    src={colorImage}
                    className="item-image"
                    alt="Product"
                  />
                </span>
              )}
              <span data-index={index} className="selected-label">
                {label}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

DropDownItems.propTypes = {
  id: PropTypes.string,
  items: PropTypes.array,
  action: PropTypes.func,
  handleClose: PropTypes.func,
  labelKey: PropTypes.string,
  disableAutoFocus: PropTypes.bool,
  keydownFocus: PropTypes.bool,
  // valueKey: PropTypes.string
}

export default DropDownItems
